import type { FilePondFile } from 'filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { FilePond, registerPlugin } from 'react-filepond';
import { QueryObserverResult } from 'react-query';
import { useSelector } from 'react-redux';
import {
  BodyText,
  TextAndIconButton,
  WYSIWYG,
} from 'src/components/atomsLegacy';
import { handleErrorToast } from 'src/components/molecules/Toast/Toast';
import { ConfirmDialog } from 'src/components/organismsLegacy';
import {
  CancelButton,
  ConfirmButton,
} from 'src/components/organismsLegacy/ConfirmDialog/ConfirmDialog.styles';
import { casePageVm } from 'src/components/pages/Case/Case.slice';
import { CaseByIdQuery, UploadSource } from 'src/graphql';
import { UseCaseDocuments, useCreateDocument } from 'src/hooks/cases';
import { ALL_MIME_TYPES, UploadScanStatus } from 'src/types';
import { validateFileUpload } from 'src/utils/validateFileUpload/fileValidation';
import { DocumentEditorHeader } from '../DocumentEditorHeader';
import {
  CANCEL_ICON_STYLES,
  DOCUMENT_EDITOR_LAYOUT_CLASS,
  DocumentEditorContainer,
  EDITOR_STYLES,
  EditorContainer,
  EditorTitleContainer,
  FileUploadContainer,
  RemoveFileIcon,
  SPINNER_OVERLAY_STYLES,
  UPLOAD_OVERLAY_STYLES,
  UploadedFileName,
  getUploadedFileStyle,
  UploadsContainer,
  UploadsListContainer,
} from './DocumentEditor.styles';

registerPlugin(FilePondPluginFileValidateSize);

interface Props {
  caseId: string;
  caseDocuments: Omit<
    UseCaseDocuments,
    'activeTab' | 'setTempActiveTab' | 'selectActiveTab'
  >;
  isSaving: boolean;
  setIsSaving: (isSaving: boolean) => void;
  refetch: () => Promise<QueryObserverResult<CaseByIdQuery, unknown>>;
  disableLegacyStyles?: boolean;
}

export const DocumentEditor: FC<Props> = ({
  caseId,
  caseDocuments,
  isSaving,
  setIsSaving,
  refetch,
  disableLegacyStyles,
}) => {
  const theme = useTheme();
  const createDocument = useCreateDocument(
    caseId,
    caseDocuments.openDocument,
    caseDocuments.setDocumentState,
    refetch,
  );

  const exitEditor = useCallback(() => {
    createDocument.setDocumentHasChanged(false);
    caseDocuments.setOpenDocumentId(null);
    caseDocuments.closeDocumentEditor();
    setIsSaving(false);
  }, [createDocument, caseDocuments, setIsSaving]);

  const [invalidFiles, setInvalidFiles] = useState<
    Array<{ name: string; type: string }>
  >([]);

  // Preserve scan statuses during save operations to prevent color flickering
  const [preservedScanStatuses, setPreservedScanStatuses] = useState<
    Record<string, UploadScanStatus>
  >({});

  // Update preserved statuses when uploads change (but not during save)
  useEffect(() => {
    if (!isSaving && createDocument.uploads) {
      const statusMap: Record<string, UploadScanStatus> = {};
      createDocument.uploads.forEach((upload) => {
        if (upload.s3path && upload.scanStatus) {
          statusMap[upload.s3path] = upload.scanStatus as UploadScanStatus;
        }
      });
      setPreservedScanStatuses(statusMap);
    }
  }, [createDocument.uploads, isSaving]);

  const showInvalidFilesToast = useCallback(() => {
    const fileNames = invalidFiles.map((file) => file.name).join(', ');
    handleErrorToast({
      toastMessage: 'Unsupported file types',
      toastDescription: `The following files have unsupported file types: ${fileNames}`,
    });
    setInvalidFiles([]);
  }, [invalidFiles]);

  useEffect(() => {
    if (invalidFiles.length > 0) {
      const timer = setTimeout(showInvalidFilesToast, 100);
      return () => clearTimeout(timer);
    }
  }, [invalidFiles, showInvalidFilesToast]);

  const [isUploading, setIsUploading] = useState(false);
  const browseInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(
    null,
  );
  const [programmaticRemovalIds, setProgrammaticRemovalIds] = useState<
    Set<string>
  >(new Set());
  const filePondRef = React.useRef<FilePond>(null);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const reduxCase = useSelector(casePageVm);

  const mimeTypeSet = useMemo(() => new Set(ALL_MIME_TYPES), []);

  // Optimized user permissions check
  const userHasReadOnlyAccess = useMemo(
    () =>
      reduxCase.permissions.canViewCaseWorkspaceTab &&
      !reduxCase.permissions.canEditCaseWorkspaceTab,
    [
      reduxCase.permissions.canEditCaseWorkspaceTab,
      reduxCase.permissions.canViewCaseWorkspaceTab,
    ],
  );

  const isFileAlreadyInvalid = (
    invalidFilesList: Array<{ name: string; type: string }>,
    fileName: string,
    fileType: string,
  ) => {
    return invalidFilesList.some(
      (f) => f.name === fileName && f.type === fileType,
    );
  };

  // Helper function to add invalid file to state
  const addInvalidFile = useCallback((fileName: string, fileType: string) => {
    setInvalidFiles((prev) => {
      return isFileAlreadyInvalid(prev, fileName, fileType)
        ? prev
        : [...prev, { name: fileName, type: fileType }];
    });
  }, []);

  // Optimized file validation function
  const validateFile = useCallback(
    (file: File): boolean => {
      // Check MIME type
      if (!mimeTypeSet.has(file.type)) {
        addInvalidFile(file.name, file.type);
        return false;
      }

      // Check if already in FilePond
      if (filePondRef.current) {
        const existingFileNames = filePondRef.current
          .getFiles()
          .map((f: FilePondFile) => f.filename);
        if (existingFileNames.includes(file.name)) {
          return false;
        }
      }

      return true;
    },
    [mimeTypeSet, addInvalidFile],
  );

  // Unified function to handle all file additions
  const handleFileAddition = useCallback(
    (files: File[]) => {
      if (!filePondRef.current) return;

      const validFiles = files.filter(validateFile);
      validFiles.forEach((file) => {
        filePondRef.current!.addFile(file);
      });
    },
    [validateFile],
  );

  const handleCloseEditor = useCallback(() => {
    if (createDocument.documentHasChanged) {
      setShowConfirmDialog(true);
    } else {
      exitEditor();
    }
  }, [createDocument.documentHasChanged, exitEditor]);

  const handleSaveEdits = useCallback(async () => {
    await caseDocuments.handleSaveDocument(
      createDocument.title,
      createDocument.value,
      createDocument.uploads.map((upload) => upload.s3path),
    );
    createDocument.setDocumentHasChanged(false);
    caseDocuments.closeDocumentEditor();
    caseDocuments.setActiveTab(caseDocuments.tempActiveTab);
    setIsSaving(false);
  }, [caseDocuments, createDocument, setIsSaving]);

  // Optimized drag handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const overlay = document.getElementById('custom-upload-overlay');
    if (overlay) overlay.style.pointerEvents = 'none';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const overlay = document.getElementById('custom-upload-overlay');
      if (overlay) overlay.style.pointerEvents = 'auto';

      const files = Array.from(e.dataTransfer.files);
      handleFileAddition(files);
    },
    [handleFileAddition],
  );

  // Optimized file input handler
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      handleFileAddition(files);
      e.target.value = ''; // Clear for re-selection
    },
    [handleFileAddition],
  );

  // Optimized cancel upload handler
  const handleCancelUpload = useCallback(() => {
    if (filePondRef.current?.getFiles().length) {
      filePondRef.current.getFiles().forEach((file: FilePondFile) => {
        if (file.status === 2) {
          // status 2 = processing
          filePondRef.current?.removeFile(file.id);
        }
      });
    }
    setIsUploading(false);
    setUploadingFileName(null);
  }, []);

  // Optimized programmatic removal state handler
  const updateProgrammaticRemovalIds = useCallback(
    (action: 'add' | 'delete', id: string) => {
      setProgrammaticRemovalIds((prev) => {
        const newSet = new Set(prev);
        action === 'add' ? newSet.add(id) : newSet.delete(id);
        return newSet;
      });
    },
    [],
  );

  // Optimized file removal function
  const removeFileFromFilePond = useCallback((fileId: string) => {
    if (filePondRef.current) {
      const files = filePondRef.current.getFiles();
      const fileIndex = files.findIndex((f: FilePondFile) => f.id === fileId);
      if (fileIndex !== -1) {
        files.splice(fileIndex, 1);
      }
    }
  }, []);
  useEffect(() => {
    if (!createDocument.documentHasChanged && isSaving) {
      caseDocuments.setActiveTab(caseDocuments.tempActiveTab);
      caseDocuments.setOpenDocumentId(null);
      caseDocuments.setCreateDocumentEditorOpen(false);
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseDocuments, createDocument.documentHasChanged, isSaving]);

  return (
    <DocumentEditorContainer
      $disableLegacyStyles={disableLegacyStyles}
      data-testid="case-document-editor"
      className={disableLegacyStyles ? DOCUMENT_EDITOR_LAYOUT_CLASS : undefined}
    >
      <EditorTitleContainer>
        <DocumentEditorHeader
          title={createDocument.title}
          setTitle={createDocument.setTitle}
          closeEditor={handleCloseEditor}
          saveDocument={async () => {
            await caseDocuments.handleSaveDocument(
              createDocument.title,
              createDocument.value,
              createDocument.uploads.map((upload) => upload.s3path),
            );
            createDocument.setDocumentHasChanged(false);
            caseDocuments.setCreateDocumentEditorOpen(false);
            exitEditor();
          }}
          documentHasChanged={createDocument.documentHasChanged}
          documentState={caseDocuments.documentState}
        />
      </EditorTitleContainer>
      <EditorContainer>
        <div style={EDITOR_STYLES}>
          <WYSIWYG
            onChange={(value) => {
              createDocument.setValue(value.markdown);
            }}
            value={createDocument.value}
            placeholder="Type or paste your text here"
            disabled={userHasReadOnlyAccess}
            framed={disableLegacyStyles}
          />
        </div>
        <div>
          <FileUploadContainer>
            <UploadsContainer data-testid="uploads-container">
              {!userHasReadOnlyAccess ? (
                <div
                  data-testid="file-upload-area"
                  style={{ position: 'relative' }}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <FilePond
                    data-testid="file-pond"
                    ref={filePondRef}
                    credits={false}
                    allowImagePreview={false}
                    allowMultiple
                    allowDrop={false}
                    server={{ process: createDocument.uploadFile }}
                    acceptedFileTypes={ALL_MIME_TYPES}
                    disabled={isUploading && !!uploadingFileName}
                    dropOnPage={false}
                    dropValidation={false}
                    beforeAddFile={(file) => {
                      try {
                        validateFileUpload({
                          filename: file.filename,
                          mimeType: file.fileType,
                        });
                        return true;
                      } catch (error) {
                        setInvalidFiles((prev) => [
                          ...prev,
                          { name: file.filename, type: file.fileType },
                        ]);
                        return false;
                      }
                    }}
                    onaddfile={(err, file) => {
                      if (!err) {
                        setUploadingFileName(file.filename);
                      }
                    }}
                    onprocessfilestart={() => setIsUploading(true)}
                    onprocessfile={(err, file) => {
                      setIsUploading(false);
                      setUploadingFileName(null);
                      if (!err) {
                        // Check if upload already exists
                        const uploadExists = createDocument.uploads.some(
                          (upload) => upload.filename === file.filename,
                        );

                        if (!uploadExists) {
                          createDocument.setUploads([
                            ...createDocument.uploads,
                            {
                              s3path: file.serverId,
                              filename: file.filename,
                              scanStatus: UploadScanStatus.PENDING,
                              uploadSource: UploadSource.ReshubWorkspace,
                            },
                          ]);
                        }

                        // Mark for programmatic removal and remove from FilePond
                        if (filePondRef.current) {
                          updateProgrammaticRemovalIds('add', file.id);
                          filePondRef.current.removeFile(file.id);
                        }
                      }
                    }}
                    onprocessfileabort={() => {
                      setIsUploading(false);
                      setUploadingFileName(null);
                    }}
                    onerror={() => {
                      setIsUploading(false);
                      setUploadingFileName(null);
                    }}
                    onremovefile={(err, file) => {
                      if (!err && file) {
                        const isProgrammaticRemoval =
                          programmaticRemovalIds.has(file.id);

                        if (isProgrammaticRemoval) {
                          updateProgrammaticRemovalIds('delete', file.id);
                        } else {
                          // User removal - remove from uploads
                          createDocument.setUploads(
                            createDocument.uploads.filter(
                              (upload) => upload.filename !== file.filename,
                            ),
                          );
                        }
                        removeFileFromFilePond(file.id);
                      }
                    }}
                  />
                  {/* Overlay for click-to-upload */}
                  {!isUploading && (
                    <div
                      id="custom-upload-overlay"
                      style={UPLOAD_OVERLAY_STYLES}
                      onClick={() => browseInputRef.current?.click()}
                    />
                  )}
                  {/* Upload spinner with cancel */}
                  {isUploading && (
                    <div style={SPINNER_OVERLAY_STYLES}>
                      <div>
                        <img
                          src="/assets/close-icon-black.svg"
                          alt="Cancel Upload"
                          style={CANCEL_ICON_STYLES}
                          onClick={handleCancelUpload}
                        />
                      </div>
                    </div>
                  )}
                  <input
                    id="document-editor-upload-input"
                    type="file"
                    multiple
                    accept={ALL_MIME_TYPES.join(',')}
                    style={{ display: 'none' }}
                    ref={browseInputRef}
                    onChange={handleFileInputChange}
                  />
                </div>
              ) : (
                <div data-testid="read-only-upload-message">
                  {/* Read-only access - no file upload available */}
                </div>
              )}
              <UploadsListContainer>
                <BodyText weight={'bold'} size={'large'}>
                  Uploaded files
                </BodyText>
                {createDocument.uploads && createDocument.uploads.length > 0 ? (
                  createDocument.uploads.map((uploadedFile, index) => {
                    // Ensure scanStatus is properly typed and has a fallback
                    // During saving, use preserved status to prevent color flickering
                    const currentScanStatus =
                      uploadedFile.scanStatus as UploadScanStatus;
                    const preservedStatus =
                      preservedScanStatuses[uploadedFile.s3path];
                    const scanStatus =
                      currentScanStatus ||
                      (isSaving && preservedStatus
                        ? preservedStatus
                        : UploadScanStatus.PENDING);

                    return (
                      <TextAndIconButton
                        style={getUploadedFileStyle(theme)}
                        key={`uploaded-file-${index}`}
                        data-testid={`document-upload-${uploadedFile.s3path}-row`}
                      >
                        <UploadedFileName
                          $status={scanStatus}
                          onClick={() =>
                            createDocument.handleOpenFile(uploadedFile)
                          }
                        >
                          {uploadedFile.filename}
                        </UploadedFileName>
                        {!userHasReadOnlyAccess && (
                          <RemoveFileIcon
                            src="/assets/close-icon-black.svg"
                            height={34}
                            width={34}
                            data-testid="remove-upload-icon"
                            alt="Close"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent click from bubbling to overlay
                              createDocument.handleOnRemoveFile(uploadedFile);
                            }}
                          />
                        )}
                      </TextAndIconButton>
                    );
                  })
                ) : (
                  <BodyText weight={'regular'}>
                    There are no files uploaded
                  </BodyText>
                )}
              </UploadsListContainer>
            </UploadsContainer>
          </FileUploadContainer>
        </div>
      </EditorContainer>

      <ConfirmDialog
        open={showConfirmDialog}
        setOpen={setShowConfirmDialog}
        title="Cancel Edits?"
        body="Are you sure you want to cancel your edits? You will not be able to recover the document later."
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={exitEditor}
        data-testid="cancel-document-edit"
        confirmButton={
          <ConfirmButton
            onClick={caseDocuments.closeDocumentEditor}
            data-testid="cancel-document-edit-confirm"
          >
            CONTINUE
          </ConfirmButton>
        }
        cancelButton={
          <CancelButton
            onClick={() => setShowConfirmDialog(false)}
            data-testid="cancel-document-edit-cancel"
          >
            CANCEL
          </CancelButton>
        }
      />
      <ConfirmDialog
        open={isSaving && createDocument.documentHasChanged}
        setOpen={setIsSaving}
        title="Leaving document editor?"
        body="Before you leave save your document."
        onCancel={() => setIsSaving(false)}
        onConfirm={handleSaveEdits}
        data-testid="cancel-document-edit"
        confirmButton={
          <ConfirmButton
            onClick={handleSaveEdits}
            data-testid="save-document-edit-confirm"
          >
            SAVE
          </ConfirmButton>
        }
        cancelButton={
          <CancelButton
            onClick={() => setIsSaving(false)}
            data-testid="save-document-edit-cancel"
          >
            CANCEL
          </CancelButton>
        }
      />
    </DocumentEditorContainer>
  );
};
